import { useSnackbar } from 'notistack';
import { useStringsContext } from '../../../../contexts/strings';
import { useCallback } from 'react';
import { useUserAuth } from '../../../../contexts/auth';
import adminApi from '../../../../api/admin-api';
import { Author, CreateAuthorInput, UpdateAuthorInput } from '../types';

export const useAuthors = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();
  const { currentUser } = useUserAuth();

  const createAuthor = useCallback(
    async (authors: CreateAuthorInput) => {
      try {
        const response = await adminApi.post({
          path: `/authors`,
          auth: await currentUser?.getIdToken(),
          body: authors,
        });

        if (!response.ok) {
          throw new Error(`Creating author failed! (${response.status})`);
        }
      } catch (error) {
        console.error(error);

        enqueueSnackbar(strings.messages.loadingError, {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, strings, currentUser]
  );

  const updateAuthor = useCallback(
    async (author: UpdateAuthorInput) => {
      try {
        const response = await adminApi.put({
          path: `/authors/${author.id}`,
          auth: await currentUser?.getIdToken(),
          body: author,
        });
        if (!response.ok) {
          throw new Error(`Updating author failed! (${response.status})`);
        }
      } catch (error) {
        console.error(error);

        enqueueSnackbar(strings.messages.loadingError, {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, strings, currentUser]
  );

  const fetchAuthors = useCallback(async (): Promise<Author[]> => {
    let authors: Author[] = [];

    try {
      const response = await adminApi.get({
        path: `/authors`,
        auth: await currentUser?.getIdToken(),
      });

      if (response.status === 404) {
        return [];
      }

      if (!response.ok) {
        throw new Error(`Fetching authors failed! (${response.status})`);
      }

      authors = await response.json();
    } catch (error) {
      console.error(error);

      enqueueSnackbar(strings.messages.loadingError, {
        variant: 'error',
      });
    }

    return authors;
  }, [enqueueSnackbar, strings, currentUser]);

  return { fetchAuthors, createAuthor, updateAuthor };
};
